import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbPlus, TbTrash, TbArrowRight } from 'react-icons/tb';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { toast } from 'react-toastify';
import ModalLayout from '../../components/layouts/ModalLayout';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import Button from '../../components/buttons/Button';
import {
  ContributionFrequency,
  ContributionPlanWithMembersModel,
  ContributionPlanWithPayments,
  ContributionPaymentWithTransactionDetails,
  CreateContributionPlanDto,
  Currency,
  JournalEntryLine,
} from '../../../types/bankAccount';

import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import * as bankAccountService from '../../../services/community/bankAccountService';
import Avatar from '../../components/misc/Avatar';
import ProgressBar from '../../components/misc/ProgressBar';
import {
  FinancialTable,
  FinancialDataCell,
} from '../../components/tables/FinancialTable';
import { formatEuroCents } from '../../../utils/bankAccountUtil';
import { formatDate } from '../../../utils/stringUtils';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import ContributionPlanFormWizard from '../financial/components/ContributionPlanFormWizard';

type ContributionPlansPageProps = {
  registerCreateModalOpener?: (open: () => void) => void;
};

export default function ContributionPlansPage({
  registerCreateModalOpener,
}: ContributionPlansPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans' });
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [plans, setPlans] = useState<ContributionPlanWithMembersModel[]>([]);
  const [plansWithPayments, setPlansWithPayments] = useState<ContributionPlanWithPayments[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRunningPlans, setIsRunningPlans] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(Currency.EUR);
  const [selectedPlan, setSelectedPlan] = useState<ContributionPlanWithPayments | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _.chain(
    _connections.filter((c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER),
  )
    .groupBy((c) => +c.dashboardMemberId)
    .mapValues((v) => v[0])
    .value();

  useEffect(() => {
    bankAccountService.getAllContributionPlans().then(setPlans);
  }, []);

  useEffect(() => {
    // Fetch detailed payment information for each plan
    const fetchPlansWithPayments = async () => {
      const plansWithPaymentsData = await Promise.all(
        plans.map((plan) =>
          bankAccountService.getContributionPlanWithPayments(plan.contributionPlanId),
        ),
      );
      setPlansWithPayments(plansWithPaymentsData);
    };

    if (plans.length > 0) {
      fetchPlansWithPayments();
    }
  }, [plans]);

  const createContributionPlan = async (plan: CreateContributionPlanDto): Promise<void> => {
    bankAccountService.createContributionPlan(plan).then((createdPlan) => {
      setPlans((prevPlans) => [
        ...prevPlans,
        {
          ...createdPlan,
          appliesTo: plan.appliesToDashboardMemberId.map((id) => ({ dashboardMemberId: id })),
        },
      ]);
      setOpenCreateModal(false);
    });
  };

  useEffect(() => {
    if (registerCreateModalOpener) {
      registerCreateModalOpener(() => setOpenCreateModal(true));
    }
  }, [registerCreateModalOpener]);

  const deleteContributionPlan = async (planId: number) => {
    try {
      await bankAccountService.deleteContributionPlan(planId);
      setPlans((prevPlans) => prevPlans.filter((p) => p.contributionPlanId !== planId));
      setPlansWithPayments((prevPlans) => prevPlans.filter((p) => p.contributionPlanId !== planId));
      toast.success(t('success.deleted'));
    } catch (error: any) {
      toast.error(t('error.deletionFailed', { error: error.message }));
    }
  };

  const handleRunContributionPlans = async () => {
    if (plans.length === 0) {
      toast.warning(t('error.noPlansToRun') || 'No contribution plans to run');
      return;
    }

    if (
      !window.confirm(
        t('confirmations.runPlans') ||
          `Run all contribution plans for ${selectedCurrency}? This will post journal entries for amounts due.`,
      )
    ) {
      return;
    }

    try {
      setIsRunningPlans(true);
      const result = await bankAccountService.runContributionPlans(selectedCurrency);
      toast.success(
        t('success.plansRun', {
          posted: result.posted,
          skipped: result.skipped,
        }) ||
          `Posted ${result.posted} contributions, skipped ${result.skipped} members who already paid`,
      );
      // Refresh plans to show updated payment status
      const updatedPlans = await bankAccountService.getAllContributionPlans();
      setPlans(updatedPlans);
    } catch (error: any) {
      console.error('Error running contribution plans:', error);
      toast.error(
        error?.response?.data?.message ||
          t('error.runFailed') ||
          'Failed to run contribution plans',
      );
    } finally {
      setIsRunningPlans(false);
    }
  };

  const handleRowClick = (planId: number) => {
    const planWithPayments = plansWithPayments.find((p) => p.contributionPlanId === planId);
    if (planWithPayments) {
      setSelectedPlan(planWithPayments);
      setIsDetailModalOpen(true);
    }
  };

  const getPaymentAmount = (payment: ContributionPaymentWithTransactionDetails): number => {
    return payment.journalEntry?.lines?.reduce((sum: number, line: JournalEntryLine) => sum + line.debit, 0) || 0;
  };

  const calculateProgress = (plan: ContributionPlanWithPayments) => {
    // Total goal is plan.amount per member × number of members
    const totalGoal = plan.amount * plan.appliesTo.length;
    const paidAmount = plan.contributionPayments.reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0,
    );
    return totalGoal > 0 ? (paidAmount / totalGoal) * 100 : 0;
  };

  const getPaidAmount = (plan: ContributionPlanWithPayments) => {
    return plan.contributionPayments.reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0,
    );
  };

  // Filter plans based on search query
  const filteredPlans = useMemo(() => {
    if (!searchQuery.trim()) {
      return plans;
    }

    const query = searchQuery.toLowerCase().trim();
    return plans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(query) || plan.description.toLowerCase().includes(query),
    );
  }, [plans, searchQuery]);

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        {plans.length > 0 && (
          <>
            <SearchBar
              className="max-w-[380px]"
              search={searchQuery}
              setSearch={setSearchQuery}
            />
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">{t('currency') || 'Currency'}:</label>
                <select
                  className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value as Currency)}>
                  <option value={Currency.EUR}>EUR</option>
                  <option value={Currency.USD}>USD</option>
                  <option value={Currency.GBP}>GBP</option>
                </select>
              </div>
              <Button
                variant="primary-dark"
                onClick={handleRunContributionPlans}
                disabled={isRunningPlans}
                className="min-w-[180px]">
                <TbArrowRight />
                {isRunningPlans ? t('running') || 'Running...' : t('runPlans') || 'Run Plans'}
              </Button>
              {!registerCreateModalOpener && (
                <PrimaryButton
                  text={t('create')}
                  icon={TbPlus}
                  onClick={() => setOpenCreateModal(true)}
                  className="min-w-[200px]"
                />
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-8">
        {plans.length === 0 ? (
          <EmptyScreenView
            title={t('noPlans')}
            subtitle={t('createFirstPlan')}
            primaryButton={
              !registerCreateModalOpener && (
                <PrimaryButton
                  text={t('createFirstButton')}
                  icon={TbPlus}
                  onClick={() => setOpenCreateModal(true)}
                />
              )
            }
          />
        ) : (
          <div className="flex flex-col gap-8">
            <FinancialTable
              columns={[
                { id: 'name', label: t('table.name') },
                { id: 'amount', label: t('table.amount') },
                { id: 'installments', label: t('table.installments') },
                { id: 'assignedTo', label: t('table.assignedTo') },
                { id: 'paid', label: t('table.paid') },
                { id: 'actions', label: t('table.actions') },
              ]}
              rows={filteredPlans}
              renderRow={(plan, index) => {
                const planWithPayments = plansWithPayments.find(
                  (p) => p.contributionPlanId === plan.contributionPlanId,
                );
                const progress = planWithPayments ? calculateProgress(planWithPayments) : 0;
                const paidAmount = planWithPayments ? getPaidAmount(planWithPayments) : 0;

                return (
                  <>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      <p className="text-sm font-medium">{plan.name}</p>
                      <p className="text-xs text-primary-300">
                        {plan.description.slice(0, 50)}
                        {plan.description.length >= 50 && '...'}
                      </p>
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      {formatEuroCents(plan.amount, i18n.language)}
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      {plan.numberOfInstallments}
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      <AvatarList
                        connections={plan.appliesTo.map(
                          ({ dashboardMemberId }) => connections[dashboardMemberId],
                        )}
                      />
                    </FinancialDataCell>
                    <FinancialDataCell onClick={() => handleRowClick(plan.contributionPlanId)}>
                      <div className="flex flex-col gap-1">
                        <ProgressBar
                          progress={progress}
                          variant="primary-light"
                          showText={false}
                        />
                        <div className="flex justify-between text-xs">
                          <span>{progress.toFixed(0)}%</span>
                          <span>{formatEuroCents(paidAmount, i18n.language)}</span>
                        </div>
                      </div>
                    </FinancialDataCell>
                    <FinancialDataCell>
                      <ActionMenu>
                        <ActionMenuButton
                          icon={TbTrash}
                          text={t('table.delete')}
                          className="text-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContributionPlan(plan.contributionPlanId);
                          }}
                        />
                      </ActionMenu>
                    </FinancialDataCell>
                  </>
                );
              }}
            />
          </div>
        )}
      </div>

      <CreateContributionPlanModal
        open={openCreateModal}
        setOpen={setOpenCreateModal}
        onSubmit={createContributionPlan}
      />

      {selectedPlan && (
        <ContributionPlanDetailModal
          plan={selectedPlan}
          open={isDetailModalOpen}
          setOpen={setIsDetailModalOpen}
          connections={connections}
          getPaymentAmount={getPaymentAmount}
        />
      )}
    </>
  );
}

function AvatarList({ connections }: { connections: Connection[] }) {
  return (
    <div className="flex items-center gap-[-8px]">
      {connections.slice(0, 2).map((c, index) => (
        <Avatar
          key={`avatar_contribution_${index}_${c?.dashboardMemberId}`}
          className={`relative z-[${10 - index}] flex h-8 w-8 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
          src={c?.picture}
          alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
        />
      ))}
      {connections.length > 2 && (
        <div
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-md"
          style={{
            marginLeft: '-8px',
            zIndex: 1,
          }}>
          +{connections.length - 2}
        </div>
      )}
    </div>
  );
}


type CreateContributionPlanModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (plan: CreateContributionPlanDto) => Promise<void>;
};

function CreateContributionPlanModal({
  open,
  setOpen,
  onSubmit,
}: CreateContributionPlanModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans.createModal' });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  const _connections = useSelector((state: RootState) => state.connections);
  const connections = _connections.filter((c) => c.type === ConnectionType.MEMBER);
  const [contributionPlan, setContributionPlan] = useState<CreateContributionPlanDto>({
    name: '',
    description: '',
    amount: 0,
    frequency: ContributionFrequency.ONCE,
    startDate: new Date(),
    numberOfInstallments: 1,
    appliesToDashboardMemberId: [],
  });
  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl">
      <div className="flex max-h-[calc(100vh-5rem)] flex-col gap-4 overflow-y-auto rounded-md bg-secondary-50 p-6">
        <h2 className="text-lg font-medium">{t('title')}</h2>
        <ContributionPlanFormWizard
          mode="create"
          initialValue={{
            name: '',
            description: '',
            amount: 0,
            frequency: ContributionFrequency.MONTHLY,
            startDate: new Date(),
            numberOfInstallments: 1,
            appliesToDashboardMemberId: [],
          }}
          onSubmit={onSubmit}
        />
      </div>
    </ModalLayout>
  );
}

type ContributionPlanDetailModalProps = {
  plan: ContributionPlanWithPayments;
  open: boolean;
  setOpen: (open: boolean) => void;
  connections: Record<number, Connection>;
  getPaymentAmount: (payment: ContributionPaymentWithTransactionDetails) => number;
};

function ContributionPlanDetailModal({
  plan,
  open,
  setOpen,
  connections,
  getPaymentAmount,
}: ContributionPlanDetailModalProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.contributionPlans' });
  // Total progress across all members
  const totalGoal = plan.amount * plan.appliesTo.length;
  const totalPaidAmount = plan.contributionPayments.reduce(
    (sum, payment) => sum + getPaymentAmount(payment),
    0,
  );
  const totalProgress = totalGoal > 0 ? (totalPaidAmount / totalGoal) * 100 : 0;
  const totalRemaining = totalGoal - totalPaidAmount;

  // Per-member progress
  const memberProgress = plan.appliesTo.map(({ dashboardMemberId }) => {
    const memberPayments = plan.contributionPayments.filter(
      (p) => p.dashboardMemberId === dashboardMemberId,
    );
    const memberPaid = memberPayments.reduce(
      (sum, payment) => sum + getPaymentAmount(payment),
      0,
    );
    const memberProgressPercent =
      plan.amount > 0 ? (memberPaid / plan.amount) * 100 : 0;

    return {
      dashboardMemberId,
      paid: memberPaid,
      progress: memberProgressPercent,
      remaining: plan.amount - memberPaid,
    };
  });

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-4xl" closeButton>
      <div className="flex max-h-[calc(100vh-5rem)] flex-col gap-6 overflow-y-auto rounded-md bg-secondary-50 p-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{plan.name}</h2>
          {plan.description && (
            <p className="mt-1 text-sm text-gray-600">{plan.description}</p>
          )}
        </div>

        {/* Plan Details */}
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-secondary-200 bg-white p-4">
          <div>
            <p className="text-xs font-medium text-gray-500">{t('table.amount')}</p>
            <p className="mt-1 text-lg font-semibold">{formatEuroCents(plan.amount, i18n.language)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">{t('table.installments')}</p>
            <p className="mt-1 text-lg font-semibold">{plan.numberOfInstallments}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">{t('createModal.frequency')}</p>
            <p className="mt-1 text-sm">{plan.frequency}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">{t('createModal.startDate')}</p>
            <p className="mt-1 text-sm">{formatDate(new Date(plan.startDate))}</p>
          </div>
        </div>

        {/* Total Progress */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              {t('totalProgress') || 'Total Progress'}
            </p>
            <p className="text-sm font-semibold">
              {formatEuroCents(totalPaidAmount, i18n.language)} /{' '}
              {formatEuroCents(totalGoal, i18n.language)}
            </p>
          </div>
          <p className="mb-2 text-xs text-gray-500">
            {formatEuroCents(plan.amount, i18n.language)}{' '}
            {t('perMemberGoal') || 'per member'} × {plan.appliesTo.length}{' '}
            {t('members') || 'members'}
          </p>
          <ProgressBar progress={totalProgress} variant="primary-light" showText={false} />
          <div className="mt-2 flex justify-between text-xs text-gray-600">
            <span>
              {totalProgress.toFixed(0)}% {t('completed') || 'completed'}
            </span>
            <span>
              {formatEuroCents(totalRemaining, i18n.language)}{' '}
              {t('remaining') || 'remaining'}
            </span>
          </div>
        </div>

        {/* Members */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <p className="mb-3 text-sm font-medium text-gray-700">{t('table.assignedTo')}</p>
          <div className="flex flex-wrap gap-2">
            {plan.appliesTo.map(({ dashboardMemberId }) => {
              const connection = connections[dashboardMemberId];
              if (!connection) return null;
              return (
                <div
                  key={dashboardMemberId}
                  className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1">
                  <Avatar
                    className="h-6 w-6 rounded-full"
                    src={connection.picture}
                    alias={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
                  />
                  <span className="text-sm">
                    {connection.fields.FIRST_NAME} {connection.fields.LAST_NAME}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Member Progress */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <p className="mb-3 text-sm font-medium text-gray-700">
            {t('memberProgress') || 'Member Progress'}
          </p>
          {memberProgress.length === 0 ? (
            <p className="py-2 text-sm text-gray-500">
              {t('noMembers') || 'No members assigned'}
            </p>
          ) : (
            <div className="space-y-3">
              {memberProgress.map(({ dashboardMemberId, paid, progress, remaining }) => {
                const connection = connections[dashboardMemberId];
                return (
                  <div
                    key={dashboardMemberId}
                    className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="flex items-center gap-3">
                      {connection && (
                        <Avatar
                          className="h-8 w-8 rounded-full"
                          src={connection.picture}
                          alias={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
                        />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {connection
                            ? `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`
                            : `Member ${dashboardMemberId}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatEuroCents(paid, i18n.language)} /{' '}
                          {formatEuroCents(plan.amount, i18n.language)}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <ProgressBar
                        progress={progress}
                        variant="primary-light"
                        showText={false}
                      />
                      <div className="mt-1 flex justify-between text-[10px] text-gray-600">
                        <span>
                          {progress.toFixed(0)}% {t('completed') || 'completed'}
        </span>
                        <span>
                          {formatEuroCents(remaining, i18n.language)}{' '}
                          {t('remaining') || 'remaining'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payment History */}
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <p className="mb-3 text-sm font-medium text-gray-700">
            {t('paymentHistory') || 'Payment History'}
          </p>
          {plan.contributionPayments.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {t('noPayments') || 'No payments yet'}
            </p>
          ) : (
            <div className="space-y-2">
              {plan.contributionPayments.map((payment) => {
                const connection = connections[payment.dashboardMemberId];
                const amount = getPaymentAmount(payment);
                const paymentDate = payment.journalEntry?.entryDate
                  ? formatDate(new Date(payment.journalEntry.entryDate))
                  : '-';

                return (
                  <div
                    key={payment.contributionPaymentId}
                    className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center gap-3">
                      {connection && (
                        <Avatar
                          className="h-8 w-8 rounded-full"
                          src={connection.picture}
                          alias={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
                        />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {connection
                            ? `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`
                            : `Member ${payment.dashboardMemberId}`}
                        </p>
                        <p className="text-xs text-gray-500">{paymentDate}</p>
                      </div>
                    </div>
                    <p className="text-sm font-semibold">{formatEuroCents(amount, i18n.language)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ModalLayout>
  );
}
